import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, Eye, LockKeyhole, LogOut, Mail, MapPin, PanelLeftClose,
  PanelLeftOpen, Search, Settings, ShieldCheck, UserCog,
} from 'lucide-react';

import { XomicsAuthProvider, useXomicsAuth } from './AuthContext';
import { tx, XomicsI18nProvider } from './i18n';
import { codeLabel, permissionLabel, roleLabel } from './labels';
import XomicsCluster from './XomicsCluster';
import XomicsCohorts from './XomicsCohorts';
import XomicsCompanies from './XomicsCompanies';
import XomicsContentCatalogue from './XomicsContent';
import XomicsPipelines from './XomicsPipelines';
import XomicsReleases from './XomicsReleases';
import XomicsProjects from './XomicsProjects';
import XomicsReports from './XomicsReports';
import ReportAnalytics from './ReportAnalytics';
import ReportLibrary from './ReportLibrary';
import XomicsUploads from './XomicsUploads';
import XomicsWebservers from './XomicsWebservers';
import MobileOverview from './MobileOverview';
import MobileAccounts from './MobileAccounts';
import MobileWearables from './MobileWearables';
import MobileUsage from './MobileUsage';
import MobileNotifications from './MobileNotifications';
import StorefrontOverview from './StorefrontOverview';
import StorefrontLive from './StorefrontLive';
import StorefrontCampaigns from './StorefrontCampaigns';
import StorefrontAcquisition from './StorefrontAcquisition';
import StorefrontSessions from './StorefrontSessions';
import StorefrontPages from './StorefrontPages';
import StorefrontProducts from './StorefrontProducts';
import StorefrontCheckout from './StorefrontCheckout';
import StorefrontAudience from './StorefrontAudience';
import StorefrontPerformance from './StorefrontPerformance';
import XomicsOverview from './XomicsOverview';
import XomicsAnalytics from './XomicsAnalytics';
import XomicsStaff from './XomicsStaff';
import XomicsPermissions from './XomicsPermissions';
import XomicsAudit from './XomicsAudit';
import FeedbackInbox from './FeedbackInbox';
import XomicsFeedback from './XomicsFeedback';
import XomicsSettings from './XomicsSettings';
import XomicsCollections from './XomicsCollections';
import XomicsSamples from './XomicsSamples';
import XomicsOrders from './XomicsOrders';
import XomicsShipments from './XomicsShipments';
import XomicsProducts from './XomicsProducts';
import XomicsCategories from './XomicsCategories';
import XomicsPolicies from './XomicsPolicies';
import XomicsShipping from './XomicsShipping';
import XomicsStoreProducts from './XomicsStoreProducts';
import XomicsStockRequests from './XomicsStockRequests';
import XomicsLocationStock from './XomicsLocationStock';
import XomicsCoupons from './XomicsCoupons';
import XomicsBundles from './XomicsBundles';
import XomicsTranslations from './XomicsTranslations';
import XomicsHelpCentre from './XomicsHelpCentre';
import XomicsHub from './XomicsHub';
import XomicsQuiz from './XomicsQuiz';
import XomicsMenu from './XomicsMenu';
import XomicsLaboratory from './XomicsLaboratory';
import XomicsLabOverview from './XomicsLabOverview';
import XomicsLabWork from './XomicsLabWork';
import XomicsLabBatch from './XomicsLabBatch';
import XomicsLabCustomers from './XomicsLabCustomers';
import LabScanStation from './LabScanStation';
import XomicsLabReceipt from './XomicsLabReceipt';
import XomicsLabWorkflows from './XomicsLabWorkflows';
import { XomicsLabAcceptanceRules, XomicsLabAnalyses } from './XomicsLabReference';
import XomicsLabDevices from './XomicsLabDevices';
import XomicsOrganizations from './XomicsOrganizations';
import XomicsLocations from './XomicsLocations';
import XomicsDevices from './XomicsDevices';
import XomicsMessaging from './XomicsMessaging';
import XomicsTransactions from './XomicsTransactions';
import XomicsFinance from './XomicsFinance';
import XomicsBillingProfiles from './XomicsBillingProfiles';
import XomicsValidation from './XomicsValidation';
import XomicsActivate from './XomicsActivate';
import CustomerPortal from './CustomerPortal';
import Placeholder from './Placeholder';
import { errorMessage } from './ui';
import { NotificationBell } from './XomicsNotifications';
import { ProfileSettings } from './XomicsProfile';
import { Head } from './parts';
import { SearchOverlay, useSearchHotkey } from './Search';
import { RECENTS_ICON, destinationsOf, visibleNav } from './nav';
import { loadRecents, recordPageView } from './api';
import { startLiveUpdates } from './liveUpdates';
import AuthFrame from './AuthFrame';
import logo from './logo.png';
import './xomics.css';

function LoginPanel() {
  const { login, startupError } = useXomicsAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(startupError || '');
  const [submitting, setSubmitting] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await login(email, password);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthFrame>
      <div className="xo-auth-form-logo">
        <img src={logo} alt="" />
      </div>
      <header className="xo-auth-card-head">
        <h1 id="xomics-login-heading">{tx("ui.shell.xomics_fc526e73")}</h1>
        <p className="xo-muted">
          {tx("ui.shell.sign_in_to_continue_to_the_secure_omics_workspace_f884edfa")}
        </p>
      </header>

        <form onSubmit={submit} className="xo-form xo-auth-form">
          <label className="xo-auth-field">
            <span><Mail aria-hidden="true" /> {tx("ui.shell.email_84add5b2")}</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              required
              disabled={submitting}
              autoFocus
            />
          </label>
          <label className="xo-auth-field">
            <span><LockKeyhole aria-hidden="true" /> {tx("ui.shell.password_8be3c943")}</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              disabled={submitting}
            />
          </label>
          {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
          <button type="submit" className="xo-btn xo-auth-submit" disabled={submitting}>
            {submitting ? tx("ui.shell.signing_in_c66b2adc") : tx("ui.shell.sign_in_ada2e9e9")}
          </button>
        </form>
    </AuthFrame>
  );
}



// The screens that exist. Anything in NAV without an entry here renders a
// placeholder that says so, which is why the tree can describe the whole
// product before the product is finished.
const BUILT = {
  overview: <XomicsOverview />,
  projects: <XomicsProjects />,
  uploads: <XomicsUploads />,
  reporting: <XomicsReports />,
  'reporting/requests': <XomicsReports />,
  'reporting/files': <ReportLibrary />,
  'reporting/analytics': <ReportAnalytics />,
  cluster: <XomicsCluster />,
  cohorts: <XomicsCohorts />,
  pipelines: <XomicsPipelines />,
  'pipelines/catalogue': <XomicsPipelines />,
  'pipelines/releases': <XomicsReleases />,
  'analytics/runs': <XomicsAnalytics />,
  analytics: <XomicsAnalytics />,
  audit: <XomicsAudit />,
  staff: <XomicsStaff />,
  companies: <XomicsCompanies />,
  'companies/directory': <XomicsCompanies view="directory" />,
  'companies/catalogue': <XomicsCompanies view="catalogue" />,
  permissions: <XomicsPermissions />,
  feedback: <XomicsFeedback />,
  'feedback-inbox': <FeedbackInbox />,
  settings: <XomicsSettings />,
  kits: <XomicsCollections />,
  'sample-tracking': <XomicsSamples />,
  orders: <XomicsOrders />,
  shipments: <XomicsShipments />,
  // The Store section: an office's own pages, then its groups, each opening on
  // its first page.
  'store/selling': <XomicsStoreProducts />,
  'store/products': <XomicsStoreProducts />,
  'store/shipping': <XomicsShipping />,
  'store/stock-requests': <XomicsStockRequests key="office" side="office" />,
  // A warehouse's and a shop's own pages.
  'warehouse/stock': <XomicsLocationStock key="warehouse" kind="warehouse" />,
  'warehouse/requests': <XomicsStockRequests key="warehouse" side="warehouse" />,
  'retail/stock': <XomicsLocationStock key="retail" kind="retail" />,
  'retail/requests': <XomicsStockRequests key="retail" side="retail" />,
  catalogue: <XomicsProducts />,
  products: <XomicsProducts />,
  categories: <XomicsCategories />,
  discounts: <XomicsCoupons />,
  coupons: <XomicsCoupons />,
  bundles: <XomicsBundles />,
  validation: <XomicsValidation />,
  'laboratory/overview': <XomicsLabOverview />,
  'laboratory/receipt': <XomicsLabReceipt />,
  'laboratory/samples': <XomicsLaboratory />,
  'laboratory/work': <XomicsLabWork key="work" />,
  'laboratory/batch': <XomicsLabBatch />,
  'laboratory/customer-submissions': <XomicsLabCustomers />,
  'laboratory/approvals': <XomicsLabWork key="approvals" approvals />,
  'laboratory/workflow-definitions': <XomicsLabWorkflows />,
  'laboratory/analyses': <XomicsLabAnalyses />,
  'laboratory/acceptance-rules': <XomicsLabAcceptanceRules />,
  'laboratory/devices': <XomicsLabDevices />,
  organizations: <XomicsOrganizations />,
  locations: <XomicsLocations />,
  devices: <XomicsDevices />,
  messaging: <XomicsMessaging />,
  'commerce/overview': <XomicsFinance key="overview" view="overview" />,
  transactions: <XomicsFinance key="ledger" view="ledger" />,
  'finance/receivables': <XomicsFinance key="receivables" view="receivables" />,
  'finance/costs': <XomicsFinance key="costs" view="costs" />,
  'finance/billing-profiles': <XomicsBillingProfiles />,
  'commerce/analytics': <XomicsTransactions view="analytics" />,
  content: <XomicsContentCatalogue />,
  webservers: <XomicsWebservers view="overview" />,
  'webservers/overview': <XomicsWebservers view="overview" />,
  'webservers/analytics': <XomicsWebservers view="analytics" />,
  mobile: <MobileOverview />,
  'mobile/overview': <MobileOverview />,
  'mobile/accounts': <MobileAccounts />,
  'mobile/wearables': <MobileWearables />,
  'mobile/usage': <MobileUsage />,
  'mobile/notifications': <MobileNotifications />,
  'store/analytics': <StorefrontOverview />,
  'store/overview': <StorefrontOverview />,
  'store/live': <StorefrontLive />,
  'store/campaigns': <StorefrontCampaigns />,
  'store/sessions': <StorefrontSessions />,
  'store/acquisition': <StorefrontAcquisition />,
  'store/audience': <StorefrontAudience />,
  'store/product-funnel': <StorefrontProducts />,
  'store/checkout-funnel': <StorefrontCheckout />,
  'store/pages': <StorefrontPages />,
  'store/performance': <StorefrontPerformance />,
  'content/languages': <XomicsContentCatalogue view="languages" />,
  'content/translations': <XomicsTranslations />,
  website: <XomicsMenu />,
  'help-centre': <XomicsHelpCentre />,
  'longevity-hub': <XomicsHub />,
  quiz: <XomicsQuiz />,
  menu: <XomicsMenu />,
  policies: <XomicsPolicies />,
};

function screenFor(destination) {
  const built = BUILT[destination.path];
  if (built) return built;
  return (
    <Placeholder
      title={destination.label}
      section={destination.parent === null
        ? destination.section
        : `${destination.section} · ${destination.parent}`}
    />
  );
}

// Who you are, as opposed to how the portal behaves for you. Reached from the
// avatar top right rather than from the sidebar, because it is about the person
// signed in rather than a place in the product.
function ProfileScreen() {
  const { user, permissions } = useXomicsAuth();
  return (
    <div className="xo-profile-page">
      <Head title={tx("ui.shell.profile_ff4fc027")} subtitle={tx("ui.shell.signed_in_as_email_eee52995", { email: user.email })} />
      <div className="xo-profile-layout">
        <ProfileSettings />
        <section className="xo-profile-access-panel">
          <header>
            <span><ShieldCheck aria-hidden="true" /></span>
            <div>
              <p className="xo-eyebrow">{tx("ui.shell.authorization_5e25ce00")}</p>
              <h2>{tx("ui.shell.what_you_can_do_fb2dbad1")}</h2>
            </div>
          </header>
          <div className="xo-profile-permissions">
            {permissions.map((permission) => (
              <span key={permission}><i aria-hidden="true" />{permissionLabel(permission)}</span>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function initials(user) {
  return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
}

// The five screens you were on last, read back out of the audit log.
//
// Two lines each: what you opened, and the page it lives under. "Project 4"
// alone does not say where it sits, and the whole point of the list is getting
// back somewhere without having to work out the route first.
function Recents({ collapsed, recorded, sections }) {
  const [open, setOpen] = useState(false);
  const [recents, setRecents] = useState([]);

  // Keyed on visits actually written, not on the URL changing.
  //
  // Both used to hang off location.pathname, which raced: this fetch went out
  // alongside the POST recording the visit and usually won, so the list came
  // back describing where you were before you moved. Waiting for the write
  // means one refetch, after the thing it is meant to show exists.
  //
  // It also means returning through this list refetches nothing at all — that
  // navigation is deliberately not recorded, so `recorded` does not move.
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    loadRecents(5)
      .then((rows) => { if (active) setRecents(rows); })
      // A sidebar convenience must never take the page down with it, and there
      // is nothing useful to say in 200px of chrome.
      .catch(() => {});
    return () => { active = false; };
  }, [open, recorded]);

  if (collapsed) return null;

  // Where each one sits, so an entry says what it is and where it lives. The
  // server sends the page and its name; the shape of the sidebar is the
  // client's own knowledge and is not worth making the server repeat.
  const whereIs = new Map(destinationsOf(sections).map((destination) => [
    `/xomics/${destination.path}`,
    destination.parent === null
      ? destination.section
      : `${destination.section} · ${destination.parent}`,
  ]));

  return (
    <div className="xo-nav-group">
      <button type="button" className="xo-nav-item" aria-expanded={open}
        onClick={() => setOpen((was) => !was)}>
        <RECENTS_ICON aria-hidden="true" />
        <span>{tx("ui.shell.recents_84380106")}</span>
        {open ? <ChevronDown className="xo-nav-chev" aria-hidden="true" />
          : <ChevronRight className="xo-nav-chev" aria-hidden="true" />}
      </button>

      {open && (
        <div className="xo-nav-kids">
          {recents.length === 0 ? (
            <p className="xo-nav-none">{tx("ui.shell.nowhere_yet_a0bc430e")}</p>
          ) : recents.map((recent) => (
            // Link, not NavLink: a recent is a shortcut, not a place in the
            // navigation. Being on the page it points at does not make the
            // shortcut "selected" -- there is nothing to select, it is a
            // quick click back to somewhere you have already been.
            // Flagged so the shell does not record it. Going back to
            // somewhere through the list of places you have been is a return,
            // not a visit -- recording it would promote the entry you pressed
            // and reshuffle the list as you use it.
            <Link key={recent.path} to={recent.path.replace('/xomics/', '')}
              state={{ viaRecents: true }}
              className="xo-nav-kid is-two">
              <strong>{recent.label}</strong>
              <span>{whereIs.get(recent.path)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// An item with children. Clicking the row opens the first child and expands it,
// which is what the chevron would have done anyway — two targets for one
// intention is a way to make people aim.
function NavParent({ item, collapsed, activePath }) {
  const holdsActive = item.children.some((child) => activePath === child.path);
  const [open, setOpen] = useState(holdsActive);
  const Icon = item.icon;

  // Following a link into a child -- from search, or from Recents -- has to
  // open the branch it landed in, or the sidebar disagrees with the page.
  useEffect(() => { if (holdsActive) setOpen(true); }, [holdsActive]);

  if (collapsed) {
    return (
      <NavLink to={item.children[0].path} title={item.label}
        className={({ isActive }) => `xo-nav-item${isActive ? ' is-on' : ''}`}>
        <Icon aria-hidden="true" />
        <span>{item.label}</span>
      </NavLink>
    );
  }

  // A toggle, and only a toggle: opening a branch navigates nowhere and lights
  // nothing up. Nothing has been chosen yet, so nothing should look chosen --
  // the highlight belongs to whichever child you actually go to.
  return (
    <div className="xo-nav-group">
      <button
        type="button"
        className="xo-nav-item"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Icon aria-hidden="true" />
        <span>{item.label}</span>
        {open ? <ChevronDown className="xo-nav-chev" aria-hidden="true" />
          : <ChevronRight className="xo-nav-chev" aria-hidden="true" />}
      </button>

      {open && (
        <div className="xo-nav-kids">
          {item.children.map((child) => (
            <NavLink key={child.path} to={child.path} className="xo-nav-kid">
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function SecureWorkspace() {
  const {
    user, permissions, logout, previewRoles, previewAsRole,
  } = useXomicsAuth();
  const [logoutError, setLogoutError] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [previewBusy, setPreviewBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => window.localStorage.getItem('xo-rail-collapsed') === 'yes',
  );
  const [searching, setSearching] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Bumped once a page view has been written. Recents watches this rather than
  // the route, so it reads the list after the visit is in it.
  const [recorded, setRecorded] = useState(0);
  const menuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // The server fixes a socket's topic entitlement when it authenticates, so a
  // different account needs a different socket. Keyed on the account uuid
  // rather than on "is signed in", which does not change when the person does.
  useEffect(() => startLiveUpdates(), [user.roleCode, user.uuid]);

  const openSearch = useCallback(() => setSearching(true), []);
  useSearchHotkey(openSearch);

  useEffect(() => {
    window.localStorage.setItem('xo-rail-collapsed', collapsed ? 'yes' : 'no');
  }, [collapsed]);

  // Close the profile menu on a click anywhere else. Pointerdown rather than
  // click so it closes before the thing underneath reacts.
  useEffect(() => {
    if (!menuOpen) return undefined;
    function onPointerDown(event) {
      if (!menuRef.current.contains(event.target)) setMenuOpen(false);
    }
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [menuOpen]);

  // Every navigation is recorded, which is what Recents reads back. Done here
  // rather than per screen so a page added later cannot forget to do it, and so
  // the screens that make no API call at all still count as visited.
  useEffect(() => {
    // Arriving from the Recents list is a return rather than a visit, and
    // recording it would float that entry to the top of the very list being
    // used to reach it.
    if (location.state !== null && location.state.viaRecents === true) return undefined;
    let active = true;
    recordPageView(location.pathname)
      // Counting writes rather than route changes is what lets Recents refetch
      // once the visit exists instead of alongside it.
      .then(() => { if (active) setRecorded((n) => n + 1); })
      // Ignored on purpose: a page must not fail to render because a record of
      // opening it could not be written, and the server refuses any path it
      // does not recognise anyway. Recents simply does not move.
      .catch(() => {});
    return () => { active = false; };
  }, [location.pathname, location.state]);

  const sections = visibleNav(permissions, user?.location?.kind ?? null);
  const destinations = destinationsOf(sections);
  const activePath = location.pathname.replace('/xomics/', '');
  // Longest match wins, so /analytics/traffic is not claimed by /analytics.
  const here = [...destinations]
    .sort((a, b) => b.path.length - a.path.length)
    .find((destination) => activePath.startsWith(destination.path));

  async function signOut() {
    setLogoutError('');
    try {
      await logout(false);
    } catch (requestError) {
      setLogoutError(errorMessage(requestError));
    }
  }

  async function changePreview(roleCode) {
    setPreviewBusy(true);
    setPreviewError('');
    try {
      await previewAsRole(roleCode);
      setMenuOpen(false);
      navigate('overview');
    } catch (requestError) {
      setPreviewError(errorMessage(requestError));
    } finally {
      setPreviewBusy(false);
    }
  }

  return (
    <div id="xo" className={`xo-shell${collapsed ? ' is-narrow' : ''}`}>
      <aside className="xo-rail">
        <div className="xo-rail-top">
          {collapsed ? (
            <button type="button" className="xo-rail-logo-btn" title={tx("ui.shell.expand_the_sidebar_563dc644")}
              aria-label={tx("ui.shell.expand_the_sidebar_563dc644")} onClick={() => setCollapsed(false)}>
              <img src={logo} alt="" className="xo-logo-img" />
              <PanelLeftOpen className="xo-logo-swap" aria-hidden="true" />
            </button>
          ) : (
            <>
              <span className="xo-brand">
                <img src={logo} alt="" className="xo-logo-img" />
                <span className="xo-wordmark">{tx("ui.shell.xomics_fc526e73")}</span>
              </span>
              <button type="button" className="xo-icon-btn" title={tx("ui.shell.collapse_the_sidebar_d9d308c0")}
                aria-label={tx("ui.shell.collapse_the_sidebar_d9d308c0")} onClick={() => setCollapsed(true)}>
                <PanelLeftClose aria-hidden="true" />
              </button>
            </>
          )}
        </div>

        <div className="xo-rail-search">
          <button type="button" className={collapsed ? 'xo-icon-btn' : 'xo-searchbox'}
            title={tx("ui.shell.quick_search_3c4cdb32")} aria-label={tx("ui.shell.quick_search_3c4cdb32")} onClick={openSearch}>
            <Search aria-hidden="true" />
            {!collapsed && (<><span>{tx("ui.shell.quick_search_8d80699a")}</span><kbd>{tx("ui.shell.ctrl_k_a784e36d")}</kbd></>)}
          </button>
        </div>

        <nav className="xo-nav" aria-label={tx("ui.shell.sections_7ff5a6da")}>
          {sections.map((group, index) => (
            <div className="xo-nav-section" key={group.section === null ? `top-${index}` : group.section}>
              {group.section !== null && !collapsed && (
                <p className="xo-nav-label">{group.section}</p>
              )}
              {group.items.map((item) => {
                const Icon = item.icon;
                if (item.children) {
                  return (
                    <NavParent key={item.path} item={item}
                      collapsed={collapsed} activePath={activePath} />
                  );
                }
                return (
                  <NavLink key={item.path} to={item.path}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) => `xo-nav-item${isActive ? ' is-on' : ''}`}>
                    <Icon aria-hidden="true" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
              {/* Recents sits with the dashboard, in the group with no heading —
                  the same place the reference puts it. */}
              {index === 0 && (
                <Recents collapsed={collapsed} recorded={recorded} sections={sections} />
              )}
            </div>
          ))}
        </nav>

        <div className="xo-rail-bottom">
          {collapsed ? (
            <span className="xo-location-mark"
              title={tx("ui.shell.name_city_03d46ca0", { name: user.location.name, city: user.location.city })}>
              <MapPin aria-hidden="true" />
            </span>
          ) : (
            <div className="xo-rail-location" title={tx("ui.shell.name_city_03d46ca0", { name: user.location.name, city: user.location.city })}>
              <MapPin aria-hidden="true" />
              <span>
                <strong>{user.location.name}</strong>
                <small>{codeLabel('locationKind', user.location.kind)} · {user.location.code} · {user.location.city}</small>
              </span>
            </div>
          )}
        </div>
      </aside>

      <div className="xo-body">
        <header className="xo-topbar">
          <div className="xo-crumbs">
            <span>{tx("ui.shell.xomics_fc526e73")}</span>
            {here && (
              <>
                <ChevronRight aria-hidden="true" />
                {here.parent !== null && <><span>{here.parent}</span><ChevronRight aria-hidden="true" /></>}
                <strong>{here.label}</strong>
              </>
            )}
          </div>

          <div className="xo-topbar-actions">
            <NotificationBell onNavigate={(route) => navigate(route)} />

            <div className="xo-menu" ref={menuRef}>
              <button type="button" className="xo-menu-btn" aria-haspopup="menu"
                aria-expanded={menuOpen} onClick={() => setMenuOpen((was) => !was)}>
                <span className="xo-avatar" aria-hidden="true">{initials(user)}</span>
                <span className="xo-menu-who">
                  <strong>{user.firstName} {user.lastName}</strong>
                  <span>{user.rolePreview
                    ? tx('ui.shell.previewing_role', { role: roleLabel(user.roleCode) })
                    : roleLabel(user.roleCode)}</span>
                </span>
                <ChevronDown aria-hidden="true" />
              </button>

              {menuOpen && (
                <div className="xo-menu-panel" role="menu">
                  <div className="xo-menu-head">
                    <strong>{user.firstName} {user.lastName}</strong>
                    <span>{user.email}</span>
                  </div>
                  {previewRoles.length > 0 && (
                    <div className="xo-role-preview">
                      <span className="xo-menu-section-label">
                        <Eye aria-hidden="true" />{tx('ui.shell.preview_as_role')}
                      </span>
                      <button type="button" disabled={previewBusy || !user.rolePreview}
                        className={!user.rolePreview ? 'is-current' : ''}
                        onClick={() => changePreview(null)}>
                        <strong>{roleLabel(user.actualRoleCode)}</strong>
                        <small>{tx('ui.shell.your_actual_role')}</small>
                      </button>
                      {previewRoles
                        .filter((role) => role.code !== user.actualRoleCode)
                        .map((role) => (
                          <button type="button" key={role.code}
                            disabled={previewBusy || user.roleCode === role.code}
                            className={user.roleCode === role.code ? 'is-current' : ''}
                            onClick={() => changePreview(role.code)}>
                            <strong>{roleLabel(role.code)}</strong>
                            <small>{role.code}</small>
                          </button>
                        ))}
                      {previewError && <p className="xo-alert t-bad" role="alert">{previewError}</p>}
                    </div>
                  )}
                  <button type="button" role="menuitem"
                    onClick={() => { setMenuOpen(false); navigate('profile'); }}>
                    <UserCog aria-hidden="true" /> {tx("ui.shell.profile_ff4fc027")}
                  </button>
                  <button type="button" role="menuitem"
                    onClick={() => { setMenuOpen(false); navigate('settings'); }}>
                    <Settings aria-hidden="true" /> {tx("ui.shell.settings_c7f73bb5")}
                  </button>
                  <button type="button" role="menuitem" className="t-bad"
                    onClick={() => { setMenuOpen(false); signOut(); }}>
                    <LogOut aria-hidden="true" /> {tx("ui.shell.sign_out_dc1649a1")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="xo-scroll">
          <div className={`xo-page${activePath.startsWith('orders') || activePath.startsWith('shipments') || activePath.startsWith('products') || ['catalogue', 'categories', 'store/products', 'store/shipping', 'store/stock-requests', 'warehouse/stock', 'warehouse/requests', 'retail/stock', 'retail/requests', 'policies'].includes(activePath) || activePath.startsWith('sample-tracking') || activePath.startsWith('uploads') || activePath === 'reporting' || activePath === 'reporting/requests' ? ' xo-page-wide' : ''}`}>
            {logoutError && <p className="xo-alert t-bad" role="alert">{logoutError}</p>}
            {/* Scans are caught on every laboratory page, wherever the cursor is. */}
            <LabScanStation active={activePath.startsWith('laboratory')}>
            <Routes>
              {destinations.map((destination) => (
                <Route key={destination.path} path={destination.path}
                  element={screenFor(destination)} />
              ))}
              <Route path="profile" element={<ProfileScreen />} />
              <Route path="laboratory/processing" element={<Navigate to="/xomics/laboratory/work" replace />} />
              <Route path="laboratory/quality-control" element={<Navigate to="/xomics/laboratory/approvals" replace />} />
              {/* The storefront analytics placeholder became the Store section. */}
              <Route path="ecommerce/storefront-analytics" element={<Navigate to="/xomics/store/overview" replace />} />
              {/* The store's analytics pages sit under one group now; the addresses of the three groups they had open their first page. */}
              <Route path="store/traffic" element={<Navigate to="/xomics/store/acquisition" replace />} />
              <Route path="store/funnels" element={<Navigate to="/xomics/store/product-funnel" replace />} />
              <Route path="store/pages-and-speed" element={<Navigate to="/xomics/store/pages" replace />} />
              {/* Anything else -- including a screen this account may not open
                  -- lands on the first one it can, not on a blank page. */}
              <Route path="*" element={<Navigate to={destinations[0].path} replace />} />
            </Routes>
            </LabScanStation>
          </div>
        </div>
      </div>

      {searching && (
        <SearchOverlay destinations={destinations} onClose={() => setSearching(false)} />
      )}
    </div>
  );
}

function XomicsContent() {
  const { status } = useXomicsAuth();
  const location = useLocation();

  // Activation is reachable signed out, signed in, or mid-boot: the person
  // following an invitation link is by definition not this browser's current
  // user, and making them wait on a session restore that will fail is pointless.
  if (location.pathname.startsWith('/xomics/activate/')) {
    return (
      <Routes>
        <Route path="activate/:token" element={<XomicsActivate />} />
      </Routes>
    );
  }

  // A customer's own portal: its people are not staff and sign in there with their own accounts.
  if (location.pathname === '/xomics/customer-portal' || location.pathname.startsWith('/xomics/customer-portal/')) {
    return (
      <Routes>
        <Route path="customer-portal/*" element={<CustomerPortal />} />
      </Routes>
    );
  }

  if (status === 'booting') {
    return (
      <main id="xo" className="xo-auth">
        <div className="xo-loader" aria-label={tx("ui.shell.restoring_secure_session_95361009")} />
      </main>
    );
  }

  if (status !== 'authenticated') {
    return (
      <Routes>
        <Route path="login" element={<LoginPanel />} />
        {/* Anything else becomes the sign-in page rather than a 404: an expired
            session should land somewhere useful. */}
        <Route path="*" element={<Navigate to="/xomics/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Signed in, so the sign-in page has nothing to offer. */}
      <Route path="login" element={<Navigate to="/xomics" replace />} />
      <Route path="*" element={<SecureWorkspace />} />
    </Routes>
  );
}

const Xomics = () => (
  <XomicsI18nProvider>
    <XomicsAuthProvider>
      <XomicsContent />
    </XomicsAuthProvider>
  </XomicsI18nProvider>
);

export default Xomics;
